"use client";
import { RichText } from "@components/cms/rich-text-renderer/RichText";
import { useMarquee } from "@hooks/useMarquee";
import { useScrollDirection } from "@hooks/useScrollDirection";
import type { UspBannerModel } from "@lib/contentful/mappers/mapUspBanner";
import { Box } from "@ecco/ui/box";
import { Flex } from "@ecco/ui/flex";
import { VisuallyHidden } from "@ecco/ui/visually-hidden";
import { forwardRef } from "@ecco/ui/theme-tools";
import {
    containerStyle,
    getRotatingMessageStyle,
    getWrapperStyle,
    marqueeContentStyle,
    marqueeTrackStyle,
    rotatingMessageContainerStyle,
    staticMessageStyle,
} from "./USPbanner.styles";
import USPMessagesList from "./components/USPMessagesList/USPMessagesList";
import { useUSPBannerConfig } from "./hooks/useUSPBannerConfig";
import { FullBleed } from "@ecco/ui/full-bleed";

const BANNER_DEADZONE = 30;

export interface IUSPBannerProps {
    uspBanner: UspBannerModel;
    backgroundColor?: string;
}

export const USPbanner = forwardRef(({ uspBanner, backgroundColor }: IUSPBannerProps, ref) => {
    const { scrollDirection, isInDeadzone } = useScrollDirection({ deadZone: BANNER_DEADZONE });
    const isVisible = isInDeadzone || scrollDirection === "up";

    const { bannerBackground, animatedContainerStyle, uspMessages, isSaleSeason } =
        useUSPBannerConfig({
            uspBanner,
            backgroundColor,
        });

    const { containerRef, contentRef, shouldScroll, marqueeProps } = useMarquee({
        speed: 50,
        pauseOnHover: true,
        gap: 100,
        enabled: true,
    });
    const { style: marqueeStyle, ...marqueeRest } = marqueeProps;

    return (
        <FullBleed>
            <Box sx={getWrapperStyle(isVisible)} role="banner">
                <Box ref={ref} w="100%" px={0}>
                    <VisuallyHidden>
                        {uspMessages.map((item, i) => (
                            <Box key={`uspMessage${i}`}>
                                <RichText data={item} />
                            </Box>
                        ))}
                    </VisuallyHidden>
                    <Flex
                        sx={{
                            ...containerStyle,
                            height: "6",
                            justifyContent: "center",
                            background: bannerBackground,
                        }}
                        data-testid="USPBanner"
                    >
                        <Box ref={containerRef} sx={animatedContainerStyle} aria-hidden="true">
                            {/* Mobile: Marquee scrolling or static message */}
                            <Box sx={marqueeTrackStyle}>
                                {shouldScroll ? (
                                    <Box
                                        {...marqueeRest}
                                        sx={{
                                            ...marqueeContentStyle,
                                            ...marqueeStyle,
                                        }}
                                    >
                                        {/* First copy */}
                                        <Box
                                            ref={contentRef}
                                            display="inline-flex"
                                            alignItems="center"
                                            {...(uspMessages.length === 1 && { mr: 10 })}
                                        >
                                            <USPMessagesList
                                                messages={uspMessages}
                                                isSaleSeason={isSaleSeason}
                                                showSeparator={uspMessages.length > 1}
                                            />
                                        </Box>
                                        {/* Second copy for seamless loop */}
                                        <Box
                                            display="inline-flex"
                                            alignItems="center"
                                            aria-hidden="true"
                                        >
                                            <USPMessagesList
                                                messages={uspMessages}
                                                keyPrefix="dup-"
                                                isSaleSeason={isSaleSeason}
                                                showSeparator={uspMessages.length > 1}
                                            />
                                        </Box>
                                    </Box>
                                ) : (
                                    <Box ref={contentRef} sx={staticMessageStyle}>
                                        <USPMessagesList
                                            messages={uspMessages}
                                            isSaleSeason={isSaleSeason}
                                            showSeparator={uspMessages.length > 1}
                                        />
                                    </Box>
                                )}
                            </Box>
                            {/* Desktop: Rotating messages */}
                            <Flex sx={rotatingMessageContainerStyle}>
                                {uspMessages.map((message, index) => (
                                    <Box
                                        key={index}
                                        sx={getRotatingMessageStyle(index, uspMessages.length)}
                                    >
                                        <RichText data={message} />
                                    </Box>
                                ))}
                            </Flex>
                        </Box>
                    </Flex>
                </Box>
            </Box>
        </FullBleed>
    );
});

export default USPbanner;
